import { msg } from "@lingui/core/macro";
import { createSignal } from "solid-js";
import { ScanBarcode } from "lucide-solid";
import { findProductByCode, isScannableCode, type ProductLike } from "@stockpilot/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";

interface BarcodeScanInputProps<T extends ProductLike> {
  products: T[];
  onScan: (product: T) => void;
  /** Mensaje cuando el código no coincide con ningún producto. */
  onMiss?: (code: string) => void;
}

/**
 * Entrada de escaneo de código de barras (Stockpilot F2).
 *
 * Funciona con lector HID (teclado: teclea el código + Enter) o entrada manual.
 * 100% offline: resuelve contra el catálogo ya sincronizado (lógica pura
 * `findProductByCode`). Al encontrar un producto, llama onScan y limpia.
 */
export function BarcodeScanInput<T extends ProductLike>(props: BarcodeScanInputProps<T>) {
  const { t } = useI18n();
  const [code, setCode] = createSignal("");
  const [notFound, setNotFound] = createSignal(false);

  function submit(e: Event) {
    e.preventDefault();
    const raw = code();
    if (!isScannableCode(raw)) {
      return;
    }
    const found = findProductByCode(props.products, raw);
    if (found) {
      setNotFound(false);
      props.onScan(found);
      setCode("");
    } else {
      setNotFound(true);
      props.onMiss?.(raw);
    }
  }

  return (
    <form class="flex flex-col gap-1" onSubmit={submit} data-testid="barcode-form">
      <label class="text-xs text-muted-foreground">{t(msg`Scan barcode or SKU`)}</label>
      <div class="flex items-center gap-2">
        <ScanBarcode class="h-5 w-5 text-muted-foreground shrink-0" />
        <input
          class="w-56 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
          placeholder={t(msg`Barcode / SKU`)}
          value={code()}
          autocomplete="off"
          onInput={(e) => {
            setCode(e.currentTarget.value);
            setNotFound(false);
          }}
          data-testid="barcode-input"
        />
        <Button type="submit" variant="ghost" size="sm" data-testid="barcode-submit-button">
          {t(msg`Find`)}
        </Button>
      </div>
      {notFound() && (
        <p class="text-xs text-danger" data-testid="barcode-not-found">
          {t(msg`No product matches that code.`)}
        </p>
      )}
    </form>
  );
}
